import { motionScale } from "./MotionSettings.ts";

const MAX_DELTA = 50;

export type UpdateFn = (dt: number) => void;
export type RenderFn = () => void;
export type IsPausedFn = () => boolean;

/** ART §6.8 hit-stop scale — ONE systematized ladder, not ad-hoc magic numbers.
 *  `micro` = normal/light hit · `short` = kill / heavy-charged shot / elite hit ·
 *  `payload` = payload / boss-tier detonation. */
export type HitStopTier = "micro" | "short" | "payload";

/** Freeze length (frames @ ~60fps) per tier. Tuned in-engine (BL-36); kept
 *  conservative so dense combat never stutters. BL-44 shortens these under load
 *  before sacrificing frame-rate — `hitStopTier` is the seam for that. */
const HIT_STOP_FRAMES: Record<HitStopTier, number> = {
  micro: 1,
  short: 3,
  payload: 6,
};

/** BL-44 Part A — idle frames a freeze *reserves* after it ends. During a
 *  freeze + this gap, an equal-or-weaker freeze request is rejected, so a
 *  kill-storm can no longer refresh the freeze faster than it clears (the
 *  "hängt total" bug). At gap 6 + a `short` (3), a perfect storm freezes at
 *  most 3 of every 9 frames → the sim always runs ≥ ⅔ of the time. A strictly
 *  higher tier (payload / board-reveal) interrupts the gap and lands at once. */
const HIT_STOP_MIN_GAP_FRAMES = 6;

/** Self-limiting hit-stop state (BL-44 Part A). Exported pure so the kill-storm
 *  de-saturation is unit-tested without a live rAF loop. Determinism-neutral:
 *  only ever *delays* updates — the sim witness drives systems directly and
 *  never sees a freeze. */
export interface HitStopState {
  /** Remaining freeze frames (render keeps running while > 0). */
  frames: number;
  /** Frames during which an equal/weaker freeze request is blocked. */
  cooldown: number;
  /** Magnitude that set the current cooldown — a strictly larger request (a
   *  higher tier) interrupts; an equal/smaller one is absorbed. */
  active: number;
}

/** Admit a freeze request under the min-gap policy. A request is taken only when
 *  no freeze is "locked in" (cooldown elapsed) OR it is a strictly higher tier
 *  than the one governing the cooldown — preserving payload/board-reveal
 *  priority while back-to-back same-tier kills cannot perpetually refresh. */
export function admitHitStop(
  state: HitStopState,
  frames: number,
  gap: number = HIT_STOP_MIN_GAP_FRAMES,
): void {
  if (state.cooldown > 0 && frames <= state.active) return;
  state.frames = Math.max(state.frames, frames);
  state.active = frames;
  state.cooldown = frames + gap;
}

/** Advance one *non-paused* frame. The cooldown always burns down; a frozen
 *  frame additionally burns a freeze frame. Returns true when this frame is
 *  frozen (the caller skips the update; render still runs). */
export function tickHitStop(state: HitStopState): boolean {
  if (state.cooldown > 0) state.cooldown--;
  if (state.frames > 0) {
    state.frames--;
    return true;
  }
  return false;
}

export class GameLoop {
  private lastTimestamp = 0;
  private running_ = false;
  private updateFn: UpdateFn;
  private renderFn: RenderFn;
  private isPausedFn: IsPausedFn = () => false;
  private frameId: number | null = null;
  private timeScale_ = 1.0;
  private slowMoRemaining = 0;
  private remainingFrames_: number | null = null;
  private hitStop_: HitStopState = { frames: 0, cooldown: 0, active: 0 };

  constructor(updateFn: UpdateFn, renderFn: RenderFn) {
    this.updateFn = updateFn;
    this.renderFn = renderFn;
  }

  get timeScale(): number {
    return this.timeScale_;
  }

  set timeScale(value: number) {
    this.timeScale_ = Math.max(0.25, Math.min(4.0, value));
  }

  /** Pause update logic for N frames while continuing to render. The raw
   *  primitive — prefer {@link hitStopTier} so impacts map to the §6.8 scale.
   *  BL-44: self-limiting (min-gap cooldown) so a kill-storm can't perpetually
   *  refresh the freeze; a strictly higher tier interrupts (keeps priority). */
  hitStop(frames: number): void {
    admitHitStop(this.hitStop_, frames);
  }

  /** Freeze the sim for the named §6.8 tier's frame count (render keeps
   *  running). Determinism-neutral: only *delays* updates, never changes sim
   *  state — the witness drives systems directly and never sees this. Honours
   *  reduced-motion (BL-45): when motion is damped, the freeze is skipped. */
  hitStopTier(tier: HitStopTier): void {
    if (motionScale() === 0) return; // reduced motion — no time-freeze
    this.hitStop(HIT_STOP_FRAMES[tier]);
  }

  /** Slow-motion effect: reduce timeScale for durationMs, then restore to 1.0 */
  slowMo(durationMs: number, scale: number = 0.3): void {
    this.timeScale_ = Math.max(0.1, scale);
    this.slowMoRemaining = durationMs;
  }

  setCallbacks(updateFn: UpdateFn, renderFn: RenderFn): void {
    this.updateFn = updateFn;
    this.renderFn = renderFn;
  }

  setIsPaused(fn: IsPausedFn): void {
    this.isPausedFn = fn;
  }

  start(): void {
    if (this.running_) return;
    this.running_ = true;
    this.lastTimestamp = performance.now();
    this.frameId = requestAnimationFrame((ts) => this.tick(ts));
  }

  stop(): void {
    this.running_ = false;
    this.remainingFrames_ = null;
    // Clear any pending freeze so it can't leak into the next run.
    this.hitStop_.frames = 0;
    this.hitStop_.cooldown = 0;
    this.hitStop_.active = 0;
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  stopAfterFrames(count: number): void {
    this.remainingFrames_ = count;
  }

  get running(): boolean {
    return this.running_;
  }

  private tick(timestamp: number): void {
    if (!this.running_) return;

    const rawDelta = timestamp - this.lastTimestamp;
    // CR-3 (B1+B2): Zero dt when paused — single source of truth for freeze.
    const paused = this.isPausedFn();
    const deltaTime = paused
      ? 0
      : Math.min(rawDelta, MAX_DELTA) * this.timeScale_;
    this.lastTimestamp = timestamp;

    // Tick slowMo timer (only when not paused)
    if (!paused && this.slowMoRemaining > 0) {
      this.slowMoRemaining -= Math.min(rawDelta, MAX_DELTA);
      if (this.slowMoRemaining <= 0) {
        this.slowMoRemaining = 0;
        this.timeScale_ = 1.0;
      }
    }

    try {
      // BL-44: a frozen frame skips the update (render still runs). The min-gap
      // cooldown only advances when not paused, so a freeze is preserved across
      // a pause exactly as before; when paused, updateFn runs with deltaTime 0
      // (CR-3 zero-dt freeze) and no freeze burns down.
      const frozen = paused ? false : tickHitStop(this.hitStop_);
      if (frozen) {
        // hit-stop: hold the sim this frame, keep rendering.
      } else {
        this.updateFn(deltaTime);
      }
      this.renderFn();
    } catch (e) {
      // B11 — log the STACK, not just the message.
      // A `[GameLoop] Frame error: … reading 'x' at renderPath` was seen once,
      // live, at the PreRun→Run transition. It has never reproduced on command,
      // so there is nothing to fix yet — but the one capture was useless for
      // locating it, because the console rendering kept only the message line
      // while the stack sat unread inside the Error object. Two candidate lines
      // in `ProjectileRenderer` fit the symptom equally well and cannot be told
      // apart without it.
      // Printing `e.stack` costs nothing on a path that only runs when a frame
      // has already failed, and turns the next occurrence — whenever it comes —
      // into a single-observation diagnosis instead of another dead end.
      const detail = e instanceof Error ? (e.stack ?? `${e.name}: ${e.message}`) : String(e);
      console.error("[GameLoop] Frame error:", detail);
    }

    if (this.remainingFrames_ !== null) {
      this.remainingFrames_--;
      if (this.remainingFrames_ <= 0) {
        this.stop();
        return;
      }
    }

    this.frameId = requestAnimationFrame((ts) => this.tick(ts));
  }
}
